import { useState, useEffect, useRef } from 'react'

interface ScrollState {
  isScrollingDown: boolean
  isAtTop: boolean
}

const SCROLL_THRESHOLD = 50 // Minimum scroll position before we can collapse
const SCROLL_DELTA = 80 // Pixels user must scroll in one direction before header changes

export const useScrollDirection = (): ScrollState => {
  const [showHeader, setShowHeader] = useState(true)
  const [isAtTop, setIsAtTop] = useState(true)
  const lastScrollY = useRef(0)
  const scrollAnchor = useRef(0) // Point where we last changed state
  const ticking = useRef(false)

  useEffect(() => {
    const updateScrollState = () => {
      const currentScrollY = window.scrollY

      // Update "at top" state
      const atTop = currentScrollY <= SCROLL_THRESHOLD
      setIsAtTop(atTop)

      // If at top, always show header
      if (atTop) {
        setShowHeader(true)
        scrollAnchor.current = currentScrollY
        lastScrollY.current = currentScrollY
        ticking.current = false
        return
      }

      // Calculate distance from anchor point
      const scrollDelta = currentScrollY - scrollAnchor.current

      // User scrolled down enough -> hide header
      if (scrollDelta > SCROLL_DELTA) {
        setShowHeader(false)
        scrollAnchor.current = currentScrollY
      }
      // User scrolled up enough -> show header
      else if (scrollDelta < -SCROLL_DELTA) {
        setShowHeader(true)
        scrollAnchor.current = currentScrollY
      }

      lastScrollY.current = currentScrollY
      ticking.current = false
    }

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollState)
        ticking.current = true
      }
    }

    // Initialize
    const initialScrollY = window.scrollY
    lastScrollY.current = initialScrollY
    scrollAnchor.current = initialScrollY
    setIsAtTop(initialScrollY <= SCROLL_THRESHOLD)
    setShowHeader(true)

    // Handle both scroll and touch events for mobile
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, []) // Empty dependency array - only run once on mount

  return { isScrollingDown: !showHeader, isAtTop }
}
